import { useEffect, useMemo, useRef, useState } from 'react'
import { useIsomorphicLayoutEffect } from 'usehooks-ts'
import { Portal } from '@headlessui/react'
import { Configure, InstantSearch, useSearchBox, useInstantSearch, useInfiniteHits } from 'react-instantsearch'
import { liteClient } from 'algoliasearch/lite'
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches'
import { router } from '@inertiajs/react'

import Insights from '@/components/search/insights'
import Autocomplete from '@/components/search/autocomplete'
import InfiniteHits from '@/components/search/infinite-hits'
import CtaLink, { buttonStyles } from '@/components/ui/cta-link'
import { useSearch } from '@/components/search-provider'

import CloseIcon from '@/icons/bwi/close.svg'
import EnterKeyIcon from '@/icons/bwi/key-enter.svg'
import UpKeyIcon from '@/icons/bwi/key-arrow-up.svg'
import DownKeyIcon from '@/icons/bwi/key-arrow-down.svg'
import EscKeyIcon from '@/icons/bwi/key-escape.svg'
import AiIcon from '@/icons/ai-icon.svg'

import { getLocale, localizedPath, useTranslations } from '@/utils/locale'
import useScrollLock from '@/utils/useScrollLock'
import { cx } from '@/utils/strings'
import { HitProps } from './types'

type SearchModalProps = {
  isOpen?: boolean
  onClose?: () => void
  /** Search input placeholder */
  placeholder?: string
  /** Restrict search to a single type */
  searchType?: string
}

export default function SearchModal(props: SearchModalProps) {
  const { setIsModalOpen } = useSearch()
  const { t } = useTranslations()
  const { placeholder = t('What are you looking for?'), isOpen = false, onClose, searchType } = props
  const { VITE_ALGOLIA_APP_ID, VITE_ALGOLIA_SEARCH_KEY, VITE_ALGOLIA_INDEX_PREFIX } = import.meta.env
  const indexName = VITE_ALGOLIA_INDEX_PREFIX + getLocale().toLocaleLowerCase().replace('-', '_')

  const searchClient = useMemo(() => liteClient(VITE_ALGOLIA_APP_ID, VITE_ALGOLIA_SEARCH_KEY), [])

  const handleClose = () => {
    setIsModalOpen(false)
    onClose?.()
  }

  // Add keyboard shortcuts
  // - Close search modal with esc
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        handleClose()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return (): void => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [])

  // Scroll lock page when the modal is open
  const { lock, unlock } = useScrollLock({ autoLock: false })
  useEffect(() => {
    if (isOpen) {
      lock()
    } else {
      unlock()
    }

    return () => {
      unlock()
    }
  }, [isOpen])

  // The overlay (scrim + content) is driven by plain CSS transitions with a PERMANENT `will-change: opacity`,
  // NOT framer-motion. framer toggles `will-change` off the instant its animation completes, which makes Chrome
  // tear down the full-viewport compositor layer and repaint for a frame — the flash. That happens to every
  // fullscreen framer opacity animation here (scrim AND content). A CSS transition never touches `will-change`,
  // so the layer is kept alive and there is nothing to tear down.
  const overlayRef = useRef<HTMLDivElement>(null)
  const [overlayMounted, setOverlayMounted] = useState(false)
  const [overlayShown, setOverlayShown] = useState(false)

  // Mount on open; keep it mounted briefly on close so the exit transition can play before unmount.
  useEffect(() => {
    if (isOpen) {
      setOverlayMounted(true)
      return
    }
    setOverlayShown(false)
    const timeout = setTimeout(() => setOverlayMounted(false), 500)
    return () => clearTimeout(timeout)
  }, [isOpen])

  // Once mounted at opacity-0, force a reflow to commit that starting value, then flip to opacity-100 so the
  // CSS enter transition always runs. (rAF timing was occasionally unreliable → the fade would be skipped.)
  useIsomorphicLayoutEffect(() => {
    if (!overlayMounted || !isOpen) return
    overlayRef.current?.getBoundingClientRect()
    setOverlayShown(true)
  }, [overlayMounted, isOpen])

  return (
    <Portal>
      {/* Whole overlay is CSS-transition driven (see note above) so no framer will-change teardown flash.
          `transition-delay` is applied only while showing, so the enter is staggered (scrim → content →
          inner) but everything fades out together on close. */}
      {overlayMounted && (
        <>
          <div
            ref={overlayRef}
            className={cx(
              'fixed inset-0 z-50 overflow-hidden bg-gradient-to-t backdrop-blur-3xl transition-opacity duration-500 ease-in [will-change:opacity]',
              'from-secondary-800/50 to-secondary-800 dark:from-gray-800/0 dark:to-gray-800',
              overlayShown ? 'opacity-100' : 'opacity-0'
            )}
            onClick={handleClose}
          />
          <div
            className={cx(
              'fixed inset-0 z-50 py-8 lg:py-16 overflow-hidden transition-opacity duration-300 ease-out [will-change:opacity]',
              overlayShown ? 'opacity-100 [transition-delay:300ms]' : 'opacity-0'
            )}
            onClick={handleClose}
          >
            <button type="button" className="absolute top-4 right-4 z-10 2xl:top-3 2xl:right-3 md:right-12 text-white">
              <CloseIcon
                title={t('Close search')}
                className="text-white w-10 h-10"
                type="button"
                onClick={handleClose}
              />
            </button>
            <div
              className={cx(
                'relative w-full max-w-screen-2xl md:mx-auto px-4 md:px-8 flex flex-col justify-between h-full max-h-[100dvh]',
                'transition-opacity duration-300 ease-out [will-change:opacity]',
                overlayShown ? 'opacity-100 [transition-delay:200ms]' : 'opacity-0'
              )}
              onClick={(e) => e.stopPropagation()}
            >
              <InstantSearch searchClient={searchClient} indexName={indexName}>
                <Configure
                  filters={searchType && `type:${searchType}`}
                  attributesToRetrieve={[
                    'type',
                    'category',
                    'url',
                    'hash',
                    'title',
                    'body',
                    'objectID',
                    'breadcrumbs',
                    'toc',
                  ]}
                />
                <SearchModalContent
                  searchClient={searchClient}
                  indexName={indexName}
                  placeholder={placeholder}
                  searchType={searchType}
                  handleClose={handleClose}
                />
              </InstantSearch>
            </div>
          </div>
        </>
      )}
    </Portal>
  )
}

type SearchModalContentProps = {
  searchClient: any
  indexName: string
  placeholder: string
  searchType?: string
  handleClose: () => void
}

// Purpose separating the content into another component is to be to access context/hooks from InstantSearch
function SearchModalContent({
  searchClient,
  indexName,
  placeholder,
  handleClose,
  searchType,
}: SearchModalContentProps) {
  const {
    setIsModalOpen,
    setIsChatOpen,
    setIsChatFullscreen,
    isAskAiEnabled,
    isFloatingChatEnabled,
    setAiQuery,
    trackChatOpen,
  } = useSearch()
  const { t } = useTranslations()
  const currentLocale = getLocale()
  const { query } = useSearchBox()
  const { currentPageHits } = useInfiniteHits()
  const { status } = useInstantSearch()
  const recentSearches = useMemo(
    () =>
      createLocalStorageRecentSearchesPlugin({
        key: 'instantsearch',
        limit: 5,
      }),
    []
  )

  const [, setActiveHit] = useState<HitProps | null>(null)

  return (
    <>
      {isAskAiEnabled && (
        <CtaLink
          className="absolute top-11 lg:top-3 right-7 md:right-11 z-10"
          asButton
          style={buttonStyles.btnSolid}
          size="Smaller"
          onClick={() => {
            if (isFloatingChatEnabled) {
              setIsModalOpen(false)
              setIsChatFullscreen(true)
              setIsChatOpen(true)
            } else {
              setIsModalOpen(false)
              router.visit(localizedPath('/ask-ai/', currentLocale), {
                onFinish: () => {
                  setAiQuery(query)
                },
              })
            }
            setAiQuery(query)
            trackChatOpen('search-modal')
          }}
        >
          <AiIcon className="size-5 mr-1 [&_*]:fill-current" />
          {t('Ask AI')}
        </CtaLink>
      )}
      <Configure distinct={10} facetingAfterDistinct clickAnalytics />
      <Insights />
      <div
        className={cx(
          'flex-none mt-8 lg:mt-0',
          (query.length !== 0 || status === 'error') && '[&_.aa-Form]:!rounded-b-none [&_.aa-Panel]:!hidden'
        )}
        style={
          {
            '--aa-search-input-height': '4rem',
            '--aa-input-icon-size': '1.5rem',
          } as any
        }
      >
        <Autocomplete
          classNames={{
            form: '!rounded-lg !border-t-0 !border-l-0 !border-r-0 !border-gray-100 dark:!border-white/10 !border-b-2 focus-within:!outline-none focus-within:!shadow-none',
            inputWrapper: 'pl-2',
            input: 'md:!text-2xl md:placeholder:!text-2xl pl-8',
            submitButton: '!pl-4',
            panel: '!relative !-top-4 !left-0 !right-auto !shadow-none !rounded-none !rounded-b-2xl',
            panelLayout: '!p-4',
            sourceHeader: '!px-2',
            clearButton: '!hidden',
            // 'w-12 h-12 [&_.aa-ClearIcon]:!w-6 [&_.aa-ClearIcon]:!h-6 w-12 h-12 [&_.aa-ClearIcon]:!w-6 [&_.aa-ClearIcon]:!h-6',
            bwSearchContainer: 'mt-0',
            bwPanelContainer: 'mt-0 ',
          }}
          searchClient={searchClient}
          placeholder={placeholder}
          indexName={indexName}
          detachedMediaQuery="none"
          shouldPanelOpen={({ state }) => state.query.length === 0}
          updateOnChange
          openOnFocus
        />
      </div>
      <InfiniteHits
        className={cx(
          'bg-white dark:bg-secondary-800 flex-1 overflow-hidden rounded-b-2xl md:rounded-b-none pb-18',
          currentPageHits.length === 0 && 'rounded-b-2xl!'
        )}
        variant="preview"
        shouldGroupResults
        showIcons={false}
        onLinkClick={() => {
          handleClose()

          // Add query to recent searches
          recentSearches.data?.addItem({
            id: query,
            label: query,
          })
        }}
        onLinkPreview={(hit) => {
          setActiveHit(hit)
        }}
        showRefinements={!searchType}
      />
      <div
        className={cx(
          'hidden md:flex h-18 bg-white dark:bg-secondary-800 rounded-b-2xl items-center justify-between px-6 py-3 border-t-2 border-gray-100 dark:border-white/10',
          (query.length === 0 || currentPageHits.length === 0) && 'md:hidden'
        )}
      >
        <div className="flex gap-8 text-xl text-gray-500 dark:text-white pl-5">
          <div className="flex items-center justify-center">
            <EnterKeyIcon className="fill-none w-7 h-7 mr-3" />
            <span>{t('select')}</span>
          </div>
          <div className="flex items-center justify-center">
            <UpKeyIcon className="fill-none w-7 h-7 mr-1" />
            <DownKeyIcon className="fill-none w-7 h-7 mr-3" />
            <span>{t('navigate')}</span>
          </div>
          <div className="flex items-center justify-center">
            <EscKeyIcon className="fill-none w-7 h-7 mr-3" />
            <span>{t('close')}</span>
          </div>
        </div>
      </div>
    </>
  )
}
